import appConfig from '../../configs/appConfig';
import awsRumActionNames from '../../consts/aws/awsRumActionNames';
import awsRumActionTypes from '../../consts/aws/awsRumActionTypes';
import badgeLevelTypes from '../../consts/loyalty/badgeLevelTypes';
import badgeTypes from '../../consts/loyalty/badgeTypes';
import loyaltyErrorTypes from '../../consts/loyalty/loyaltyErrorTypes';
import milestoneTypes from '../../consts/loyalty/milestoneTypes';
import quizTypes from '../../consts/loyalty/quizTypes';
import rewardsTabTypes from '../../consts/loyalty/rewardsTabTypes';
import {strCode} from '../../consts/strCode';
import badgesConfig from '../../containers/ClubPage/configs/badgesConfig';
import {selectIsAuthorized, selectUserId} from '../../state/selectors/auth';
import {selectIsOnline} from '../../state/selectors/global';
import {
    selectBadgeData,
    selectBadges,
    selectBadgeTypeByMilestone,
    selectIsBadgesInProgress,
    selectIsQuizCompleted,
    selectIsQuizInProgress,
    selectQuizData,
} from '../../state/selectors/loyalty';
import {
    addMilestoneToBadge,
    setBadges,
    setBadgesIsProgress,
    setConversionStatus,
    setIsBadgesLoaded,
    setLoyaltyUserData,
    setRestoredQuizData,
    setRewards,
} from '../../state/slice/loyalty';
import {
    showClubPagePromptCompletedModal,
    showClubPagePromptRewardsModal,
    showClubPageQuizModal,
    showClubPageReportModal,
    showClubPageStories,
} from '../../state/slice/modal';
import {dispatch, getState} from '../../state/store';
import helpers from '../../utils/helpers';
import rumCustomEvents from '../events/rumCustomEvents';
import indexedDBService from '../indexedDBService';
import log from '../logger/log';
import networkStatusService from '../networkStatusService';
import notificationService from '../notificationService';
import uamClientService from '../uam/uamClientService';
import uamMappingService from '../uam/uamMappingService';
import * as loyaltyHelpers from './loyaltyHelpers';
import completionMessageQueue from './loyaltyMessageQueueService';
import loyaltyQueueService from './loyaltyQueueService';
import loyaltySourcingService from './loyaltySourcingService';

const RETRY_TIMEOUT = 30 * 1000;

const retryTimers = new Map();

const getBadgeData = async ({isThrowError, isSkipErrorHandling} = {}) => {
    const badgesFromStore = selectBadges(getState());
    const isBadgesInProgress = selectIsBadgesInProgress(getState());

    if (isBadgesInProgress) {
        log.info('loyaltyService: Badge data fetch already in progress, skipping duplicate call.');
        return;
    }

    if (Object.keys(badgesFromStore)?.length === 0) {
        getStoredBadgeData();
    }

    try {
        log.info('loyaltyService: Start getting badge data');

        dispatch(setBadgesIsProgress(true));

        const badgeData = await uamClientService.getUserBadges({isThrowError, isWaitingCache: true});

        if (badgeData) {
            log.info('loyaltyService: Successfully received badge data from server. Updating store.');
            dispatch(setBadges(badgeData));

            // Recalculate badges with latest server data from cache.
            // The current implementation of checkBadgeUnlockRules in userBadgesMapping uses stale state,
            // leading to incorrect lock conditions. Refactor needed to pass server data directly
            // and remove state dependency.
            log.info('loyaltyService: Recalculating badge lock conditions with latest server data.');
            await getStoredBadgeData(false);
        }
    } catch (e) {
        log.error('loyaltyService: Error during getting badge data', e?.message);

        if (isSkipErrorHandling) throw e;

        notificationService.showToastNotification({messageStr: strCode.CLUB_PAGE_GET_BADGES_ERROR});

        await getStoredBadgeData();
    } finally {
        dispatch(setBadgesIsProgress(false));
        dispatch(setIsBadgesLoaded(true));
    }
};

const getRewardsList = async () => {
    try {
        const badgeData = await uamClientService.getUserBadges({isThrowError: true});

        if (!selectIsOnline(getState())) notificationService.showOutdatedDataMessage();

        dispatch(setRewards(getGroupedRewards(badgeData)));
    } catch (e) {
        log.error('loyaltyService: Error during getting rewards list', e);

        const badgeData = await uamClientService.getUserBadges({isCached: true, isThrowError: true});

        dispatch(setRewards(getGroupedRewards(badgeData)));

        notificationService.showOutdatedDataMessage();
    }
};

const getGroupedRewards = (badgeData) => {
    return Object.values(badgeData).reduce((accumulator, reward) => proceedRewardItem(accumulator, reward), {
        [rewardsTabTypes.AVAILABLE]: [],
        [rewardsTabTypes.NOT_EARNED]: [],
        [rewardsTabTypes.REDEEMED]: [],
    });
};

const proceedRewardItem = (accumulator, reward) => {
    const {isBadgeAchieved, isRewardReceived, isCompleted, isSubBadge, badgeType} = reward || {};

    const isRewardDisabled = checkIsRewardDisabled(badgeType);

    if (isRewardReceived === undefined || isRewardDisabled) return accumulator;

    const isSubBadgeAchieved = isSubBadge && isCompleted;
    const isAchieved = isBadgeAchieved || isSubBadgeAchieved;

    if (isAchieved && isRewardReceived) {
        accumulator[rewardsTabTypes.REDEEMED].push(reward);
    } else if (isAchieved) {
        accumulator[rewardsTabTypes.AVAILABLE].push(reward);
    } else {
        accumulator[rewardsTabTypes.NOT_EARNED].push(reward);
    }

    return accumulator;
};

const checkIsRewardDisabled = (badgeType) => {
    const isSwitchMasterBadgeAvailable = loyaltyHelpers.getIsSwitchMasterBadgeAvailable();

    if (isSwitchMasterBadgeAvailable) return false;

    const badgesToCheck = [
        badgeLevelTypes.SWITCH_MASTER_LEVEL_1,
        badgeLevelTypes.SWITCH_MASTER_LEVEL_2,
        badgeLevelTypes.SWITCH_MASTER_LEVEL_3,
    ];

    return badgesToCheck.includes(badgeType);
};

const getStoredBadgeData = async (isThrowError = true) => {
    try {
        log.debug('loyaltyService: Start getting cached badge data');

        const badgeData = await uamClientService.getUserBadges({isCached: true, isThrowError});

        if (badgeData) {
            dispatch(setBadges(badgeData));
        }
    } catch (e) {
        log.debug('loyaltyService: Error during getting cached badge data', e);

        dispatch(setBadges(uamMappingService.userBadgesMapping()));
    }
};

const scheduleRetry = ({badgeType, milestone, isSilent}) => {
    const key = `${badgeType}:${milestone}`;

    clearTimeout(retryTimers.get(key));

    const id = setTimeout(() => {
        retryTimers.delete(key);

        loyaltyQueueService.addMilestoneToQueueSafe({
            isSilent,
            badgeType,
            isRetry: true,
            milestoneType: milestone,
        });
    }, RETRY_TIMEOUT);

    retryTimers.set(key, id);
};

const cancelAllBadgesRetries = () => {
    for (const id of retryTimers.values()) clearTimeout(id);
    retryTimers.clear();
};

const sendBadgeData = async ({badgeType, milestone, isSilent, isRetry}) => {
    const body = loyaltyHelpers.transformBadgeToBody({badgeType, milestone});

    try {
        if (!(await checkSendBadgeDataPreconditions(isRetry))) return;

        log.info('loyaltyService: Start updating badge data');

        await uamClientService.postUserBadges(body);

        await onSendDataSuccess(badgeType, milestone, isSilent);
    } catch (e) {
        log.error('loyaltyService: Error during updating badge data', e);

        const errorType = e?.response?.data?.error;

        if (
            errorType === loyaltyErrorTypes.MILESTONE_ALREADY_COMPLETED ||
            errorType === loyaltyErrorTypes.BADGE_ALREADY_ACHIEVED
        ) {
            await onSendDataSuccess(badgeType, milestone, true);
            return;
        }

        if (!isRetry) {
            notificationService.showPostBadgesError(false);
        }

        completionMessageQueue.rejectPending(milestone, e);

        scheduleRetry({badgeType, milestone, isSilent});
    }
};

const checkSendBadgeDataPreconditions = async (isRetry) => {
    if (!selectIsAuthorized(getState())) return false;

    if (!selectIsOnline(getState())) {
        if (!isRetry) {
            notificationService.showPostBadgesError(true);
        }
        await networkStatusService.waitOnline();
    }

    await loyaltyHelpers.waitPreconditions();

    return true;
};

const onSendDataSuccess = async (badgeType, milestone, isSilent) => {
    dispatch(addMilestoneToBadge(milestone, badgeType));
    await loyaltyQueueService.deleteBadgeFromQueue(badgeType);

    rumCustomEvents.recordAction({
        action: awsRumActionTypes.MILESTONE,
        rumActionName: awsRumActionNames.COMPLETE,
        milestoneType: milestone,
        badgeType,
    });

    if (isSilent) {
        completionMessageQueue.add(milestone);
    } else {
        showMessageOfCompleted(badgeType, milestone);
    }

    // Note: Send analytics event for PWA installation at the moment the user is awarded the “Install PWA” badge.
    if (milestone === milestoneTypes.ADD_APP_TO_HOME_SCREEN) {
        rumCustomEvents.recordAction({
            action: awsRumActionTypes.CREATED,
            rumActionName: awsRumActionNames.SHORTCUT,
        });
    }
};

const subscribeToMilestoneCompletion = async ({milestoneType, timeout = 5_000, isQuizType}) => {
    const badgeType = selectBadgeTypeByMilestone(milestoneType)(getState());

    if (!badgeType) return;

    try {
        await Promise.all([completionMessageQueue.waitForCompletion(milestoneType), helpers.timeout(timeout)]);

        if (!completionMessageQueue.has(milestoneType)) return;

        showMessageOfCompleted(badgeType, milestoneType, isQuizType);

        completionMessageQueue.delete(milestoneType);
    } catch (e) {
        log.error('loyaltyService: Show delayed milestone message error', milestoneType, e);
    }
};

const showMessageOfCompleted = (badgeType, milestoneType, isQuizType) => {
    const {isCompleted, isSubBadge, parentBadgeType, level = 'level1'} = selectBadgeData(badgeType)(getState());

    if (isSubBadge && isCompleted) {
        const {isCompleted: isParentBadgeCompleted} = selectBadgeData(parentBadgeType)(getState());
        const isSwitchMaster = parentBadgeType === badgeTypes.SWITCH_MASTER;

        if (isSwitchMaster && isParentBadgeCompleted) {
            dispatch(showClubPageReportModal());
            return;
        }
    }

    if (isCompleted) {
        rumCustomEvents.recordAction({
            action: awsRumActionTypes.RECEIVED,
            rumActionName: awsRumActionNames.BADGE,
            level,
            badgeType,
            milestoneType,
        });

        dispatch(showClubPagePromptRewardsModal({badgeType}));
        return;
    }
    rumCustomEvents.recordAction({
        action: awsRumActionTypes.RECEIVED,
        rumActionName: awsRumActionNames.MILESTONE,
        level,
        badgeType,
        milestoneType,
    });

    if (!isQuizType) {
        dispatch(showClubPagePromptCompletedModal({milestoneType, badgeType}));
    }
};

const completeMilestone = async (milestoneType, isSilent) => {
    await loyaltyHelpers.waitPreconditions(milestoneType);

    const badges = selectBadges(getState());
    const queue = loyaltyQueueService.getQueue();

    Object.entries(badgesConfig).forEach(([badgeType, cfg]) => {
        if (!cfg.milestones?.includes(milestoneType)) return;

        const storeBadge = badges[badgeType] ?? {completedMilestones: []};
        const queuedBadge = queue.find((q) => q.badge === badgeType);
        const alreadyQueued = !!queuedBadge?.milestones?.includes(milestoneType);
        const alreadyFinished = storeBadge?.completedMilestones?.includes(milestoneType);
        const isOrderImportant = storeBadge?.isOrderImportant && storeBadge?.isLocked;

        if (alreadyFinished || alreadyQueued) return;

        const isNextInOrder = !isOrderImportant
            ? true
            : cfg.milestones.findIndex((m) => m === milestoneType) === storeBadge.completedMilestones.length;

        if (!isNextInOrder) return;

        if (isSilent) {
            completionMessageQueue.waitForCompletion(milestoneType);
        }

        loyaltyQueueService.addMilestoneToQueueSafe({badgeType, milestoneType, isSilent});
    });
};

const CONVERSION_QUIZ_DB_NAME = 'ConversionQuizDB';
const CONVERSION_QUIZ_DB_STORE = 'ConversionQuizStore';
const CONVERSION_KEY_PATH = 'userId';

const saveConversionQuizData = async () => {
    const userId = selectUserId(getState());
    const quizData = selectQuizData(quizTypes.SWITCH_PROGRAM_QUIZ)(getState());

    if (!userId || !quizData) return;

    try {
        await indexedDBService.setDatabaseData(CONVERSION_QUIZ_DB_NAME, CONVERSION_QUIZ_DB_STORE, CONVERSION_KEY_PATH, {
            userId,
            quizData,
        });

        log.debug('loyaltyService: Saved conversion quiz data', quizData);
    } catch (e) {
        log.error('loyaltyService: Error during saving conversion quiz data', e);
    }
};

const restoreSavedConversionQuizData = async () => {
    const userId = selectUserId(getState());

    if (!userId) return;

    const savedQuizData = await indexedDBService.getDatabaseItemById(
        CONVERSION_QUIZ_DB_NAME,
        CONVERSION_QUIZ_DB_STORE,
        CONVERSION_KEY_PATH,
        userId
    );

    const quizData = savedQuizData?.quizData;

    if (!quizData) return;

    dispatch(setRestoredQuizData(quizTypes.SWITCH_PROGRAM_QUIZ, savedQuizData.quizData));

    log.debug('loyaltyService: Restored conversion quiz data', quizData);

    await indexedDBService.deleteDatabaseItemById(
        CONVERSION_QUIZ_DB_NAME,
        CONVERSION_QUIZ_DB_STORE,
        CONVERSION_KEY_PATH,
        userId
    );

    return true;
};

const getStoredUserConversionStatus = async () => {
    try {
        log.debug('loyaltyService: Start getting cached user conversion status');

        const {conversionStatus, userData} = await uamClientService.getUserConversionStatus({
            isCached: true,
            isThrowError: true,
        });

        dispatch(setConversionStatus(conversionStatus));
        dispatch(setLoyaltyUserData(loyaltySourcingService.mapUserSegmentTypes(userData)));
    } catch (e) {
        log.debug('loyaltyService: Error during getting cached user conversion status', e);
    }
};

const getUserConversionStatus = async () => {
    try {
        const isSwitchMasterAvailable = appConfig.getIsSwitchMasterEnabled();

        if (!isSwitchMasterAvailable) {
            log.info('loyaltyService: Switch Master feature is not available');
            return;
        }

        log.info('loyaltyService: Start getting user conversion status');

        const {conversionStatus, userData} = await uamClientService.getUserConversionStatus({
            isCached: false,
            isThrowError: true,
        });

        dispatch(setConversionStatus(conversionStatus));
        dispatch(setLoyaltyUserData(loyaltySourcingService.mapUserSegmentTypes(userData)));
    } catch (e) {
        log.error('loyaltyService: Error during getting user conversion status', e);

        notificationService.showToastNotification({
            messageStr: strCode.CLUB_CONVERSION_STATUS_ERROR_MESSAGE,
        });

        await getStoredUserConversionStatus();
    }
};

const updateConversionData = async () => {
    const useConversionData = selectQuizData(quizTypes.SWITCH_PROGRAM_QUIZ)(getState());

    try {
        log.debug('loyaltyService: Start sending user conversion data', useConversionData);

        const formattedData = loyaltyHelpers.formatConversionData(useConversionData.selectedOptions);

        await uamClientService.postConversionData(formattedData);

        const {conversionStatus, userData} = await uamClientService.getUserConversionStatus({
            isCached: false,
            isThrowError: true,
        });

        dispatch(setConversionStatus(conversionStatus));
        dispatch(setLoyaltyUserData(loyaltySourcingService.mapUserSegmentTypes(userData)));
        // NOTE: Remove after loyalty refactoring
        await getBadgeData({isThrowError: true, isSkipErrorHandling: true});

        return conversionStatus;
    } catch (e) {
        log.error('loyaltyService: Error during sending user conversion data', e?.message);

        notificationService.showRequestError();

        throw e;
    }
};

const openSwitchMilestoneContent = ({storiesType, quizType}) => {
    if (storiesType && quizType) {
        const isQuizInProgress = selectIsQuizInProgress(quizType)(getState());
        const isQuizCompleted = selectIsQuizCompleted(quizType)(getState());

        if (isQuizInProgress || isQuizCompleted) {
            dispatch(showClubPageQuizModal({quizType}));
            return;
        }

        dispatch(showClubPageStories({storiesType}));
        return;
    }

    if (storiesType) {
        dispatch(showClubPageStories({storiesType}));
        return;
    }

    if (quizType) {
        dispatch(showClubPageQuizModal({quizType}));
    }
};

export default {
    getBadgeData,
    sendBadgeData,
    completeMilestone,
    getStoredBadgeData,
    updateConversionData,
    showMessageOfCompleted,
    saveConversionQuizData,
    getUserConversionStatus,
    openSwitchMilestoneContent,
    restoreSavedConversionQuizData,
    subscribeToMilestoneCompletion,
    getRewardsList,
    cancelAllBadgesRetries,
};
